import { Button, Icon, Typography, ZoomControls } from '@/components/common';
import { ModalHeader } from '@/components/dashboard';
import Icons from '@/const/icons';
import useModal from '@/hooks/useModal';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	onBack: () => void;
	title: string;
	instructions: string;
	referenceImageUrl: string | null;
}

export const PreviewInstructionsModal = ({
	isOpen,
	onClose,
	onBack,
	title,
	instructions,
	referenceImageUrl,
}: Props) => {
	const { Modal, show, hide } = useModal();
	const scrollRef = useRef<HTMLDivElement>(null);
	const mobileScrollRef = useRef<HTMLDivElement>(null);
	const [imageLoaded, setImageLoaded] = useState(false);

	useEffect(() => {
		setImageLoaded(false);
	}, [referenceImageUrl]);

	useEffect(() => {
		if (isOpen) {
			show();
		} else {
			hide();
		}
	}, [isOpen]);

	const handleClose = () => {
		hide();
		onClose();
	};

	const referenceImage = (
		<>
			{!imageLoaded && referenceImageUrl && (
				<div className="w-full h-[360px] rounded-xl bg-gray-100 animate-pulse" />
			)}
			{referenceImageUrl && (
				<Image
					src={referenceImageUrl}
					alt="Reference"
					width={500}
					height={400}
					className={`w-full h-auto object-contain transition-opacity duration-300 ${
						imageLoaded ? 'opacity-100' : 'opacity-0 absolute'
					}`}
					onLoad={() => setImageLoaded(true)}
				/>
			)}
		</>
	);

	const externalLinkSlot = referenceImageUrl ? (
		<Link
			href={referenceImageUrl}
			target="_blank"
			rel="noopener noreferrer"
			className="rounded-full shadow-shadow2 bg-gray-5 border p-1 cursor-pointer flex items-center justify-center shrink-0"
		>
			<Icon src={Icons.externalLink} width={20} height={20} />
		</Link>
	) : null;

	return (
		<Modal
			classNameCardContainer="w-full xl:!w-[1000px] !px-4 !py-1 !pt-8"
			classNameContainerChildren="!px-0 !py-0 max-h-[85vh] 2xl:max-h-[80vh] flex flex-col"
			withXButton
			onClose={onBack}
			backgroundColor="white"
			title={
				<ModalHeader
					title={title}
					classNameContent="md:!justify-start lg:pl-12 text-left whitespace-break-spaces pr-4"
					classNameImage="!block"
				/>
			}
		>
			{/* —— Desktop: original side-by-side layout (unchanged) —— */}
			<div className="hidden lg:block">
				<Button
					label="Back"
					size="fit"
					icon={Icons.arrowLeft}
					iconLeft
					className="border-none !p-0 mb-6 ml-0 lg:ml-10 !justify-start"
					classNameLabel="!font-normal"
					onClick={onBack}
					dataCY="proof-preview-back"
				/>
				<div
					data-cy="proof-preview-layout"
					className="flex gap-x-4 lg:ml-10 lg:mr-8 mb-6"
				>
					<div
						data-cy="proof-preview-reference"
						className="flex flex-col w-full min-w-[380px] p-4 border border-grey-100 rounded-xl"
					>
						<Typography type="regular-16" className="text-black text-left">
							Reference
						</Typography>
						<div className="flex flex-col md:flex-row gap-6 h-full flex-1 overflow-hidden mt-3.5 w-full">
							<div className="flex-1 flex flex-col gap-3 min-w-0 h-full">
								<div className="flex-1 bg-white relative rounded-xl h-[400px] overflow-hidden">
									<ZoomControls
										scrollRef={scrollRef}
										height="100%"
										scrollable={false}
									>
										<div className="w-full h-full flex items-center justify-center bg-white min-h-[360px]">
											{referenceImage}
										</div>
									</ZoomControls>
									{referenceImageUrl && (
										<Link
											href={referenceImageUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="rounded-full shadow-shadow2 bg-gray-5 border p-1 absolute bottom-4 right-44 cursor-pointer"
										>
											<Icon src={Icons.externalLink} width={20} height={20} />
										</Link>
									)}
								</div>
							</div>
						</div>
					</div>
					<div
						data-cy="proof-preview-instructions"
						className="flex flex-col flex-1 w-full max-w-72"
					>
						<div className="flex flex-col bg-grey-2 lg:bg-white p-4 flex-1 border border-grey-100 rounded-xl">
							<div className="w-full md:w-auto flex-1 flex flex-col gap-3 min-h-0">
								<Typography type="regular-16" className="text-black text-left">
									Instructions
								</Typography>
								<div className="flex-1 overflow-y-auto scroll-custom max-h-[600px]">
									<Typography
										type="regular-16"
										className="text-opacity-black whitespace-pre-wrap break-words text-left"
									>
										{instructions}
									</Typography>
								</div>
							</div>
						</div>

						<div className="flex items-center justify-between gap-x-2 mt-4">
							<Button
								type="button"
								label="Cancel"
								size="fit"
								decoration="secondary"
								className="!h-[30px]"
								onClick={onBack}
								dataCY="proof-preview-cancel"
							/>
							<Button
								type="button"
								label="Continue"
								size="fit"
								decoration="primary"
								className="!h-[30px]"
								onClick={handleClose}
								dataCY="proof-preview-continue"
							/>
						</div>
					</div>
				</div>
			</div>

			{/* —— Mobile / tablet: stacked mockup; actions pinned below scroll —— */}
			<div className="flex lg:hidden flex-col flex-1 min-h-0 max-h-[75vh]">
				<div
					data-cy="proof-preview-layout"
					className="flex flex-col gap-4 px-4 overflow-y-auto scroll-custom flex-1 min-h-0"
				>
					<div
						data-cy="proof-preview-instructions"
						className="flex flex-col w-full min-w-0"
					>
						<div className="flex flex-col bg-grey-2 p-4 border border-grey-100 rounded-xl min-h-0">
							<div className="w-full flex flex-col gap-3 min-h-0">
								<Typography type="regular-16" className="text-black text-left">
									Instructions
								</Typography>
								<div className="overflow-y-auto scroll-custom max-h-[240px]">
									<Typography
										type="regular-16"
										className="text-opacity-black whitespace-pre-wrap break-words text-left"
									>
										{instructions}
									</Typography>
								</div>
							</div>
						</div>
					</div>

					<div
						data-cy="proof-preview-reference"
						className="flex flex-col w-full min-w-0"
					>
						<Typography type="regular-16" className="text-black text-left">
							Reference
						</Typography>
						<div className="flex flex-col gap-3 min-w-0 mt-3.5 px-3.5 py-4 border border-grey-100 rounded-xl">
							<div className="bg-white relative rounded-xl h-[394px] overflow-hidden">
								<ZoomControls
									scrollRef={mobileScrollRef}
									height="100%"
									scrollable={false}
									alwaysShowControls
									centerControls
									controlsEndSlot={externalLinkSlot}
								>
									<div className="w-full h-full flex items-center justify-center bg-white">
										{!imageLoaded && referenceImageUrl && (
											<div className="w-full h-full rounded-xl bg-gray-100 animate-pulse" />
										)}
										{referenceImageUrl && (
											<Image
												src={referenceImageUrl}
												alt="Reference"
												width={500}
												height={400}
												className={`max-w-full max-h-full w-auto h-auto object-contain transition-opacity duration-300 ${
													imageLoaded ? 'opacity-100' : 'opacity-0 absolute'
												}`}
												onLoad={() => setImageLoaded(true)}
											/>
										)}
									</div>
								</ZoomControls>
							</div>
						</div>
					</div>
				</div>

				<div className="shrink-0 flex items-center justify-between gap-x-2 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-white border-t border-grey-100">
					<Button
						type="button"
						label="Cancel"
						size="fit"
						decoration="secondary"
						className="!h-[48px]"
						onClick={onBack}
						dataCY="proof-preview-cancel-mobile"
					/>
					<Button
						type="button"
						label="Continue"
						size="fit"
						decoration="primary"
						className="!h-[48px]"
						onClick={handleClose}
						dataCY="proof-preview-continue-mobile"
					/>
				</div>
			</div>
		</Modal>
	);
};
